import { Link } from 'react-router-dom';
import { Folder, FileText, ChevronRight } from 'lucide-react';
import { Category } from '@/services/api';

interface CategoryCardProps {
  category: Category;
}

export function CategoryCard({ category }: CategoryCardProps) {
  const iconMap: Record<string, typeof Folder> = {
    folder: Folder,
    file: FileText,
  };

  const Icon = category.icon ? iconMap[category.icon] || Folder : Folder;

  return (
    <Link
      to={`/category/${category.slug}`}
      className="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all group"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
          <Icon className="h-6 w-6 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">
            {category.name}
          </h3>
          {category.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{category.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3">
            {category.articleCount !== undefined && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {category.articleCount} {category.articleCount === 1 ? 'article' : 'articles'}
              </span>
            )}
            {category.children && category.children.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {category.children.length} subcategories
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0 dark:text-gray-500" />
      </div>

      {/* Subcategories Preview */}
      {category.children && category.children.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {category.children.slice(0, 3).map((child) => (
              <span
                key={child.id}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded"
              >
                {child.name}
              </span>
            ))}
            {category.children.length > 3 && (
              <span className="text-xs px-2 py-1 text-gray-500 dark:text-gray-400">
                +{category.children.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}
    </Link>
  );
}
